// src/components/CreatorRoute.js
import React, { useState, useEffect } from 'react';
import { Navigate } from 'react-router-dom';
import { doc, getDoc } from 'firebase/firestore';
import { db, auth } from '../firebase';

/**
 * Protected route component for creator-only pages
 * Checks if user has a valid creator profile in Firestore
 */
export default function CreatorRoute({ children }) {
  const [loading, setLoading] = useState(true);
  const [isCreator, setIsCreator] = useState(false);
  const [user, setUser] = useState(null);

  // Helper function to check if user is admin
  const isAdmin = async (currentUser) => {
    if (!currentUser) return false;

    // Check hardcoded admin emails
    const adminEmails = [
      'matthew@backpass.co',
      'mgriff7@gmail.com',
      'mhgbackpass@gmail.com',
      'mhgcommercial@gmail.com'
    ];

    if (adminEmails.includes(currentUser.email)) {
      return true;
    }

    // Check user document for isAdmin flag
    try {
      const userDoc = await getDoc(doc(db, 'users', currentUser.uid));
      if (userDoc.exists() && userDoc.data().isAdmin === true) {
        return true;
      }
    } catch (error) {
      console.error('Error checking admin status:', error);
    }

    return false;
  };

  useEffect(() => {
    const checkCreatorStatus = async () => {
      try {
        const currentUser = auth.currentUser;

        if (!currentUser) {
          setLoading(false);
          return;
        }

        setUser(currentUser);

        // Check if user is admin - admins can access creator pages
        const userIsAdmin = await isAdmin(currentUser);
        if (userIsAdmin) {
          console.log('Admin access granted to creator route');
          setIsCreator(true);
          setLoading(false);
          return;
        }

        // Check if user has isCreator flag in users collection
        const userDoc = await getDoc(doc(db, 'users', currentUser.uid));

        if (userDoc.exists() && userDoc.data().isCreator === true) {
          setIsCreator(true);
        } else {
          setIsCreator(false);
        }
      } catch (error) {
        console.error('Error checking creator status:', error);
        setIsCreator(false);
      } finally {
        setLoading(false);
      }
    };

    // Listen for auth state changes
    const unsubscribe = auth.onAuthStateChanged((user) => {
      if (user) {
        checkCreatorStatus();
      } else {
        setLoading(false);
        setIsCreator(false);
        setUser(null);
      }
    });

    return () => unsubscribe();
  }, []);

  if (loading) {
    return (
      <div style={{
        position: 'fixed',
        top: 0,
        left: 0,
        right: 0,
        bottom: 0,
        backgroundColor: 'rgba(7, 34, 49, 0.95)',
        display: 'flex',
        flexDirection: 'column',
        justifyContent: 'center',
        alignItems: 'center',
        zIndex: 10000
      }}>
        <div style={{
          fontSize: '48px',
          marginBottom: '20px',
          animation: 'spin 1s linear infinite'
        }}>
          ⏳
        </div>
        <h2 style={{
          color: '#FFD700',
          marginBottom: '10px'
        }}>
          Verifying Creator Status...
        </h2>
        <style>
          {`
            @keyframes spin {
              from { transform: rotate(0deg); }
              to { transform: rotate(360deg); }
            }
          `}
        </style>
      </div>
    );
  }

  // Not logged in - redirect to sign in
  if (!user) {
    return <Navigate to="/signin" replace />;
  }

  // Not a creator - redirect to creator payment page
  if (!isCreator) {
    return (
      <div style={{
        position: 'fixed',
        top: 0,
        left: 0,
        right: 0,
        bottom: 0,
        backgroundColor: 'rgba(0, 0, 0, 0.8)',
        display: 'flex',
        justifyContent: 'center',
        alignItems: 'center',
        zIndex: 10000
      }}>
        <div style={{
          backgroundColor: 'white',
          padding: '40px',
          borderRadius: '10px',
          textAlign: 'center',
          maxWidth: '500px',
          boxShadow: '0 4px 20px rgba(0, 0, 0, 0.3)'
        }}>
          <div style={{
            fontSize: '48px',
            marginBottom: '20px'
          }}>
            🔒
          </div>
          <h2 style={{
            marginBottom: '15px',
            color: '#333'
          }}>
            Creator Access Required
          </h2>
          <p style={{
            fontSize: '16px',
            color: '#666',
            marginBottom: '25px',
            lineHeight: '1.6'
          }}>
            You need to be an approved BackPass creator to access this page.
            If you've already paid but don't have access, please complete your creator profile.
          </p>
          <div style={{ display: 'flex', gap: '10px', justifyContent: 'center', flexWrap: 'wrap' }}>
            <button
              onClick={() => window.location.href = '/creator-special/gemma'}
              style={{
                padding: '12px 24px',
                backgroundColor: '#FFD700',
                color: '#072231',
                border: 'none',
                borderRadius: '8px',
                cursor: 'pointer',
                fontSize: '16px',
                fontWeight: 'bold'
              }}
            >
              Become a Creator
            </button>
            <button
              onClick={() => window.location.href = '/creator-info'}
              style={{
                padding: '12px 24px',
                backgroundColor: '#667eea',
                color: 'white',
                border: 'none',
                borderRadius: '8px',
                cursor: 'pointer',
                fontSize: '16px',
                fontWeight: 'bold'
              }}
            >
              Complete Profile
            </button>
            <button
              onClick={() => window.location.href = '/'}
              style={{
                padding: '12px 24px',
                backgroundColor: '#f0f0f0',
                border: 'none',
                borderRadius: '8px',
                cursor: 'pointer',
                fontSize: '16px'
              }}
            >
              Go Home
            </button>
          </div>
        </div>
      </div>
    );
  }

  // User is an approved creator - render the protected content
  return children;
}
